import { useTranslation } from 'react-i18next';

import { CONNECTOR_LOGOS } from '~/components/logos/connector-logos';
import { cn } from '~/utils/cn';

import { catalogLabel } from '../utils/catalog-labels';
import { toStorageConnector } from '../utils/lineage-images-format';

interface Props {
  /** Any spelling the APIs use — wire (`GMC`) or storage (`GOOGLE_MERCHANT`). */
  code: string;
  /** Also print the name beside the logo (lists where the mark alone may be unfamiliar). */
  showName?: boolean;
  className?: string;
}

/**
 * A connector as its brand logo — the same marks the old Catalog pages use (`CONNECTOR_LOGOS`) —
 * with the name in the tooltip and for screen readers. Normalises the code first: the logo map is
 * keyed by storage codes while runs carry the wire code, so a raw lookup silently missed. A
 * connector with no bundled logo falls back to its name.
 */
export function ConnectorMark({ code, showName = false, className }: Props) {
  const { t } = useTranslation('catalog-enrichment');
  const name = catalogLabel(t, 'connector', code);
  const Logo = CONNECTOR_LOGOS[toStorageConnector(code)] ?? CONNECTOR_LOGOS[code.toUpperCase()];

  if (!Logo) return <span className={className}>{name}</span>;
  return (
    <span className={cn('inline-flex items-center gap-1.5 align-middle', className)} title={name}>
      <Logo className="size-4 shrink-0" />
      {showName ? <span>{name}</span> : <span className="sr-only">{name}</span>}
    </span>
  );
}
